/**
 * CommentActivityModal - View comment rollup details from activity feed
 *
 * Opens when clicking a multi-target "added N comments" activity.
 * Shows comments grouped by photo with thumbnails and reply inputs.
 */
import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { useQueries, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  useApiClient,
  formatRelativeTime,
  type ActivityItem,
  type Comment,
} from '@wovendays/api-client';
import { useTranslation } from '@wovendays/i18n';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
// Reply icon available but not used currently

interface CommentActivityModalProps {
  activity: ActivityItem | null;
  open: boolean;
  onClose: () => void;
}

export function CommentActivityModal({ activity, open, onClose }: CommentActivityModalProps) {
  const { t } = useTranslation('activity');
  const apiClient = useApiClient();
  const queryClient = useQueryClient();

  const targetIds = activity?.targetIds ?? [];

  // Fetch comments for each target media in parallel
  const commentQueries = useQueries({
    queries: targetIds.map((mediaId) => ({
      queryKey: ['comments', mediaId],
      queryFn: () => apiClient.get<{ comments: Comment[] }>(`/api/media/${mediaId}/comments`),
      enabled: open && !!activity,
    })),
  });

  const isLoading = commentQueries.some(q => q.isLoading);

  // Group comments by media, sorted by most recent first
  const photoGroups = targetIds.map((mediaId, i) => {
    const target = activity?.targets.find(t => t.id === mediaId);
    const comments = commentQueries[i]?.data?.comments ?? [];
    const allComments = comments.flatMap(c => [c, ...(c.replies ?? [])]);
    const mostRecent = allComments.length > 0
      ? Math.max(...allComments.map(c => c.createdAt))
      : 0;
    return { mediaId, gridUrl: target?.gridUrl ?? null, comments, mostRecent };
  }).sort((a, b) => b.mostRecent - a.mostRecent);

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-serif">
            {t('commentedOnPhotos', { userName: activity?.userName, count: targetIds.length })}
          </DialogTitle>
        </DialogHeader>

        {isLoading ? (
          <div className="flex justify-center py-8" role="status" aria-label={t('loadingComments')}>
            <div className="h-6 w-6 animate-spin rounded-full border-2 border-muted border-t-primary-600" />
          </div>
        ) : photoGroups.every(g => g.comments.length === 0) ? (
          <p className="py-8 text-center text-sm text-muted-foreground">
            {t('commentsUnavailable')}
          </p>
        ) : (
          <div className="space-y-6">
            {photoGroups.map(({ mediaId, gridUrl, comments }) => (
              comments.length > 0 && (
                <PhotoCommentGroup
                  key={mediaId}
                  mediaId={mediaId}
                  gridUrl={gridUrl}
                  comments={comments}
                  apiClient={apiClient}
                  queryClient={queryClient}
                />
              )
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

interface PhotoCommentGroupProps {
  mediaId: string;
  gridUrl: string | null;
  comments: Comment[];
  apiClient: any;
  queryClient: any;
}

function PhotoCommentGroup({ mediaId, gridUrl, comments, apiClient, queryClient }: PhotoCommentGroupProps) {
  const { t } = useTranslation('activity');
  const [replyContent, setReplyContent] = useState('');

  const replyMutation = useMutation({
    mutationFn: (content: string) =>
      apiClient.post(`/api/media/${mediaId}/comments`, { content }),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['comments', mediaId] });
    },
  });

  const handleReply = (e: FormEvent) => {
    e.preventDefault();
    if (!replyContent.trim()) return;
    replyMutation.mutate(replyContent.trim());
    setReplyContent('');
  };

  return (
    <div className="rounded-xl border p-4">
      <div className="flex gap-3 mb-3">
        <Link to={`/photo/${mediaId}`} className="shrink-0">
          {gridUrl ? (
            <img
              src={gridUrl}
              alt=""
              className="h-14 w-14 rounded-lg object-cover hover:opacity-80 transition-opacity"
            />
          ) : (
            <div className="h-14 w-14 rounded-lg bg-muted flex items-center justify-center text-muted-foreground text-xs">
              📷
            </div>
          )}
        </Link>
        <div className="flex-1 min-w-0 space-y-2">
          {comments.map((comment) => (
            <div key={comment.id}>
              <ModalComment comment={comment} />
              {comment.replies?.length > 0 && (
                <div className="mt-1 space-y-1">
                  {comment.replies.map((reply) => (
                    <ModalComment key={reply.id} comment={reply} isReply />
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
      <form onSubmit={handleReply} className="flex gap-2">
        <Input
          type="text"
          value={replyContent}
          onChange={(e) => setReplyContent(e.target.value)}
          placeholder={t('replyPlaceholder')}
          maxLength={1000}
          className="flex-1 rounded-full text-sm"
        />
        <Button
          type="submit"
          disabled={!replyContent.trim() || replyMutation.isPending}
          size="sm"
          className="rounded-full"
        >
          {t('common:reply')}
        </Button>
      </form>
    </div>
  );
}

function ModalComment({ comment, isReply }: { comment: Comment; isReply?: boolean }) {
  return (
    <div className={isReply ? 'ml-6 border-l-2 border-muted pl-3' : ''}>
      <div className="flex items-baseline gap-2">
        <span className="text-sm font-medium text-foreground">{comment.authorName}</span>
        <span className="font-mono text-xs text-muted-foreground">
          {formatRelativeTime(comment.createdAt)}
        </span>
      </div>
      <p className="text-sm text-foreground/80">{comment.content}</p>
    </div>
  );
}
